export type SupportedFontId =
  | 'sourceSans3'
  | 'inter'
  | 'openSans'
  | 'ibmPlexSans'
  | 'poppins'
  | 'montserrat'

export type FontUsage = 'table' | 'ui' | 'title'
export type NumericSuitability = 'tabular' | 'proportional'

export type FontResolutionDiagnosticCode =
  | 'unsupported-family'
  | 'invalid-font-weight'
  | 'unsupported-font-weight'
  | 'unsupported-font-load'

export type FontResolutionDiagnostic = {
  code: FontResolutionDiagnosticCode
  usage: FontUsage
  message: string
  requestedFamily?: string | null
  requestedWeight?: string | null
  resolvedFamily?: string | null
  resolvedWeight?: string | null
}

export type SupportedFontDefinition = {
  id: SupportedFontId
  label: string
  family: string
  weights: number[]
  usages: FontUsage[]
  numeric: NumericSuitability
}

export type ResolvedFontChoice = {
  requestedFamily: string | null
  resolvedFamily: string
  mappedFamily: string
  stack: string
  numeric: NumericSuitability
  baseWeight: number
  source: 'default' | 'direct' | 'tableau-alias' | 'unsupported'
  diagnostics: FontResolutionDiagnostic[]
}

export type ResolvedFontWeight = {
  requestedWeight: string | null
  resolvedWeight: string | null
  source: 'default' | 'explicit' | 'tableau-semantic' | 'fallback' | 'invalid' | 'unsupported'
  diagnostics: FontResolutionDiagnostic[]
}

const SYSTEM_FALLBACKS = ['"Segoe UI"', 'Roboto', 'Arial', 'sans-serif']
const DEFAULT_FONT_BASE_WEIGHT = 400

export const SUPPORTED_FONT_REGISTRY: SupportedFontDefinition[] = [
  {
    id: 'sourceSans3',
    label: 'Source Sans 3',
    family: 'Source Sans 3',
    weights: [300, 400, 500, 600, 700],
    usages: ['table', 'ui'],
    numeric: 'tabular',
  },
  {
    id: 'inter',
    label: 'Inter',
    family: 'Inter',
    weights: [300, 400, 500, 600, 700],
    usages: ['table', 'ui'],
    numeric: 'tabular',
  },
  {
    id: 'openSans',
    label: 'Open Sans',
    family: 'Open Sans',
    weights: [400, 500, 600, 700],
    usages: ['table', 'ui'],
    numeric: 'tabular',
  },
  {
    id: 'ibmPlexSans',
    label: 'IBM Plex Sans',
    family: 'IBM Plex Sans',
    weights: [300, 400, 500, 600, 700],
    usages: ['table', 'ui'],
    numeric: 'tabular',
  },
  {
    id: 'poppins',
    label: 'Poppins',
    family: 'Poppins',
    weights: [400, 500, 600, 700],
    usages: ['title', 'ui'],
    numeric: 'proportional',
  },
  {
    id: 'montserrat',
    label: 'Montserrat',
    family: 'Montserrat',
    weights: [400, 500, 600, 700],
    usages: ['title', 'ui'],
    numeric: 'proportional',
  },
]

export const DEFAULT_TABLE_FONT_ID: SupportedFontId = 'sourceSans3'
export const DEFAULT_UI_FONT_ID: SupportedFontId = 'sourceSans3'
export const DEFAULT_TITLE_FONT_ID: SupportedFontId = 'poppins'

const TABLEAU_FONT_MAPPINGS: Record<string, { fontId: SupportedFontId; baseWeight: number }> = {
  tableau: { fontId: DEFAULT_TABLE_FONT_ID, baseWeight: 400 },
  'tableau bold': { fontId: DEFAULT_TABLE_FONT_ID, baseWeight: 700 },
  'tableau book': { fontId: DEFAULT_TABLE_FONT_ID, baseWeight: 400 },
  'tableau regular': { fontId: DEFAULT_TABLE_FONT_ID, baseWeight: 400 },
  'tableau light': { fontId: DEFAULT_TABLE_FONT_ID, baseWeight: 300 },
  'tableau medium': { fontId: DEFAULT_TABLE_FONT_ID, baseWeight: 500 },
  'tableau semibold': { fontId: DEFAULT_TABLE_FONT_ID, baseWeight: 600 },
  'tableau semi bold': { fontId: DEFAULT_TABLE_FONT_ID, baseWeight: 600 },
  'tableau semi-bold': { fontId: DEFAULT_TABLE_FONT_ID, baseWeight: 600 },
}

function quoteFamily(raw: string): string {
  const family = String(raw ?? '').trim().replace(/^['"]+|['"]+$/g, '')
  if (!family) return ''
  return family.includes(' ') ? `"${family}"` : family
}

function normalizeFamilyName(raw: any): string {
  return String(raw ?? '')
    .trim()
    .replace(/^['"]+|['"]+$/g, '')
    .toLowerCase()
}

export function getSupportedFontById(id: SupportedFontId): SupportedFontDefinition {
  const match = SUPPORTED_FONT_REGISTRY.find((font) => font.id === id)
  if (!match) return SUPPORTED_FONT_REGISTRY[0]
  return match
}

export function getSupportedFontByFamily(raw: string | null | undefined): SupportedFontDefinition | null {
  const key = normalizeFamilyName(raw)
  if (!key) return null
  return SUPPORTED_FONT_REGISTRY.find((font) => normalizeFamilyName(font.family) === key) || null
}

export function getDefaultFontDefinition(usage: FontUsage): SupportedFontDefinition {
  if (usage === 'title') return getSupportedFontById(DEFAULT_TITLE_FONT_ID)
  if (usage === 'ui') return getSupportedFontById(DEFAULT_UI_FONT_ID)
  return getSupportedFontById(DEFAULT_TABLE_FONT_ID)
}

export function getDefaultFontFamily(usage: FontUsage): string {
  return getDefaultFontDefinition(usage).family
}

export function buildSupportedFontStack(family: string, usage: FontUsage): string {
  const orderedFamilies = [
    family,
    ...SUPPORTED_FONT_REGISTRY
      .filter((font) => font.family !== family && font.usages.includes(usage))
      .map((font) => font.family),
  ]
  return [...orderedFamilies.map(quoteFamily).filter(Boolean), ...SYSTEM_FALLBACKS].join(', ')
}

export function getDefaultFontStack(usage: FontUsage): string {
  return buildSupportedFontStack(getDefaultFontDefinition(usage).family, usage)
}

export function getPrimaryFontFamily(raw: string | null | undefined): string {
  const stack = String(raw ?? '').trim()
  if (!stack) return ''
  const parts = stack
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  const first = parts[0] || ''
  return first.replace(/^['"]+|['"]+$/g, '').trim()
}

export function resolveSupportedFontChoice(raw: string | null | undefined, usage: FontUsage): ResolvedFontChoice {
  const requestedFamily = getPrimaryFontFamily(raw || '')
  const requestedKey = normalizeFamilyName(requestedFamily)
  const direct = SUPPORTED_FONT_REGISTRY.find((font) => normalizeFamilyName(font.family) === requestedKey)
  if (direct) {
    return {
      requestedFamily: requestedFamily || null,
      resolvedFamily: direct.family,
      mappedFamily: direct.family,
      stack: buildSupportedFontStack(direct.family, usage),
      numeric: direct.numeric,
      baseWeight: DEFAULT_FONT_BASE_WEIGHT,
      source: 'direct',
      diagnostics: [],
    }
  }
  const mappedEntry = TABLEAU_FONT_MAPPINGS[requestedKey]
  if (mappedEntry) {
    const mapped = getSupportedFontById(mappedEntry.fontId)
    return {
      requestedFamily: requestedFamily || null,
      resolvedFamily: mapped.family,
      mappedFamily: mapped.family,
      stack: buildSupportedFontStack(mapped.family, usage),
      numeric: mapped.numeric,
      baseWeight: mappedEntry.baseWeight,
      source: 'tableau-alias',
      diagnostics: [],
    }
  }
  const defaultFont = getDefaultFontDefinition(usage)
  const diagnostics: FontResolutionDiagnostic[] = requestedFamily
    ? [{
      code: 'unsupported-family',
      usage,
      requestedFamily,
      resolvedFamily: defaultFont.family,
      message: `Unsupported font family "${requestedFamily}" for ${usage} usage; falling back to ${defaultFont.family}.`,
    }]
    : []
  return {
    requestedFamily: requestedFamily || null,
    resolvedFamily: defaultFont.family,
    mappedFamily: defaultFont.family,
    stack: buildSupportedFontStack(defaultFont.family, usage),
    numeric: defaultFont.numeric,
    baseWeight: DEFAULT_FONT_BASE_WEIGHT,
    source: requestedFamily ? 'unsupported' : 'default',
    diagnostics,
  }
}

function getSupportedWeightsForFamily(raw: string | null | undefined): number[] {
  const supported = getSupportedFontByFamily(raw)
  return Array.isArray(supported?.weights) ? supported!.weights : []
}

function parseCanonicalFontWeight(raw: string | null | undefined): { weight: number; relativeToBase: boolean; semantic: boolean } | null {
  const s = String(raw ?? '').trim().toLowerCase()
  if (!s) return null
  if (/^\d{3}$/.test(s)) {
    return { weight: Number.parseInt(s, 10), relativeToBase: false, semantic: false }
  }
  if (s === 'light') return { weight: 300, relativeToBase: false, semantic: true }
  if (s === 'normal' || s === 'regular' || s === 'book') return { weight: 400, relativeToBase: true, semantic: true }
  if (s === 'medium') return { weight: 500, relativeToBase: true, semantic: true }
  if (s === 'semibold' || s === 'semi-bold') return { weight: 600, relativeToBase: true, semantic: true }
  if (s === 'bold') return { weight: 700, relativeToBase: true, semantic: true }
  return null
}

export function resolveSupportedFontWeight(
  familyRaw: string | null | undefined,
  weightRaw: string | null | undefined,
  usage: FontUsage,
  fallbackRaw?: string | null,
): ResolvedFontWeight {
  const choice = resolveSupportedFontChoice(familyRaw, usage)
  const supportedWeights = getSupportedWeightsForFamily(choice.resolvedFamily)
  const adjustWeight = (parsed: { weight: number; relativeToBase: boolean; semantic: boolean }): number => {
    const shifted = parsed.relativeToBase
      ? (parsed.weight + (choice.baseWeight - DEFAULT_FONT_BASE_WEIGHT))
      : parsed.weight
    return Math.round(shifted)
  }
  const resolveSupportedWeight = (
    target: number,
    requestedWeight: string | null,
    source: ResolvedFontWeight['source'],
    diagnostics: FontResolutionDiagnostic[],
  ): ResolvedFontWeight => {
    if (!supportedWeights.length || supportedWeights.includes(target)) {
      return {
        requestedWeight,
        resolvedWeight: String(target),
        source,
        diagnostics,
      }
    }
    return {
      requestedWeight,
      resolvedWeight: null,
      source: 'unsupported',
      diagnostics: [
        ...diagnostics,
        {
          code: 'unsupported-font-weight',
          usage,
          requestedFamily: choice.requestedFamily,
          requestedWeight,
          resolvedFamily: choice.resolvedFamily,
          message: `Unsupported font weight "${requestedWeight}" for ${choice.resolvedFamily}; expected one of ${supportedWeights.join(', ')}.`,
        },
      ],
    }
  }
  const baseDiagnostics = [...choice.diagnostics]
  const requestedWeight = String(weightRaw ?? '').trim() || null
  const explicit = parseCanonicalFontWeight(requestedWeight)
  if (explicit) {
    return resolveSupportedWeight(
      adjustWeight(explicit),
      requestedWeight,
      explicit.semantic ? 'tableau-semantic' : 'explicit',
      baseDiagnostics,
    )
  }
  if (requestedWeight) {
    return {
      requestedWeight,
      resolvedWeight: null,
      source: 'invalid',
      diagnostics: [
        ...baseDiagnostics,
        {
          code: 'invalid-font-weight',
          usage,
          requestedFamily: choice.requestedFamily,
          requestedWeight,
          resolvedFamily: choice.resolvedFamily,
          message: `Invalid font weight "${requestedWeight}" for ${choice.resolvedFamily}.`,
        },
      ],
    }
  }
  const fallbackWeight = String(fallbackRaw ?? '').trim() || null
  const fallback = parseCanonicalFontWeight(fallbackWeight)
  if (fallback) {
    return resolveSupportedWeight(
      adjustWeight(fallback),
      fallbackWeight,
      'fallback',
      baseDiagnostics,
    )
  }
  return resolveSupportedWeight(choice.baseWeight, null, 'default', baseDiagnostics)
}

export function getSupportedFontFamilyOptions(usages: FontUsage[] = ['table', 'ui', 'title']): Array<{ value: string; label: string }> {
  return SUPPORTED_FONT_REGISTRY
    .filter((font) => usages.some((usage) => font.usages.includes(usage)))
    .map((font) => ({
      value: font.family,
      label: font.label,
    }))
}

